import React from "react";
import dayjs from "dayjs";
import {
  Flex, Spinner, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
} from "@chakra-ui/react";
import Card from "horizon/components/card/Card";
import { eventTypeLabel } from "constants/eventTypeLabels";
import ContactCardStatusChip from "./ContactCardStatusChip";
import { HEAD_CELL, BODY_CELL, ROW } from "./contactCardsManageStyles";

const COLUMNS = ["Statut", "Client", "Artiste / Groupe", "Événement", "Montant", "Créée le"];

/**
 * Formats an ISO date as DD/MM/YYYY HH:mm or "—" when missing/invalid.
 * @param {string} value - ISO date string
 * @returns {string}
 */
function formatDateTime(value) {
  if (!value) return "—";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Formats an amount with its currency (currency lives on the nested payment object).
 * @param {Object} card - Contact card
 * @returns {string}
 */
function formatAmount(card) {
  if (card?.amount === null || card?.amount === undefined) return "—";
  const currency = card?.payment?.currency || "";
  return `${card.amount} ${currency.toUpperCase()}`.trim();
}

/**
 * Builds the "Client" cell content (name + lastname + email if available).
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function ClientCell({ card }) {
  const nameColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const sub = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const fullName = [card?.name, card?.lastname].filter(Boolean).join(" ") || "—";
  const email = card?.reservation?.email || "";
  return (
    <Flex direction="column" minW="0">
      <Text fontSize="sm" fontWeight="600" color={nameColor}>{fullName}</Text>
      {email && <Text noOfLines={1} fontSize="xs" color={sub}>{email}</Text>}
    </Flex>
  );
}

/**
 * Builds the "Artiste / Groupe" cell from the enriched artist_profile field.
 * Falls back to raw artist_id/artist_group_id when the profile isn't populated.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function ArtistCell({ card }) {
  const nameColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const sub = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const name = card?.artist_profile?.artist_name || card?.reservation?.artist_name;
  const userType = card?.artist_profile?.user_type || card?.reservation?.artist_type;
  const fallbackId = card?.artist_group_id ? `Groupe #${card.artist_group_id}` : (card?.artist_id ? `Artiste #${card.artist_id}` : "—");
  return (
    <Flex direction="column" minW="0">
      <Text fontSize="sm" fontWeight="500" color={nameColor}>{name || fallbackId}</Text>
      {userType && <Text fontSize="xs" color={sub}>{userType === "artist_group" ? "Groupe" : "Artiste solo"}</Text>}
    </Flex>
  );
}

/**
 * Builds the "Événement" cell from the nested reservation fields.
 * @param {Object} props - card
 * @returns {React.ReactElement}
 */
function EventCell({ card }) {
  const titleColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const sub = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const r = card?.reservation || {};
  const title = r.title || eventTypeLabel(r.event) || "—";
  const dateStr = r.event_date ? dayjs(r.event_date).format("DD/MM/YYYY") : null;
  return (
    <Flex direction="column" minW="0">
      <Text noOfLines={1} fontSize="sm" fontWeight="500" color={titleColor}>{title}</Text>
      {dateStr && <Text fontSize="xs" color={sub}>{dateStr}</Text>}
    </Flex>
  );
}

/**
 * Renders a single contact card row.
 * @param {Object} props - card, onSelect
 * @returns {React.ReactElement}
 */
function ContactRow({ card, onSelect }) {
  const color = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
  const hoverBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
  return (
    <Tr {...ROW} _hover={{ bg: hoverBg }} onClick={() => onSelect?.(card)}>
      <Td {...BODY_CELL}><ContactCardStatusChip card={card} /></Td>
      <Td {...BODY_CELL}><ClientCell card={card} /></Td>
      <Td {...BODY_CELL}><ArtistCell card={card} /></Td>
      <Td {...BODY_CELL}><EventCell card={card} /></Td>
      <Td {...BODY_CELL} color={color}>{formatAmount(card)}</Td>
      <Td {...BODY_CELL} color={color}>{formatDateTime(card?.created_at)}</Td>
    </Tr>
  );
}

/**
 * Centered state message (loading spinner / error / empty).
 * @param {Object} props - loading (boolean), children (message node)
 * @returns {React.ReactElement}
 */
function StateBox({ loading, children }) {
  return <Flex align="center" justify="center" py="40px">{loading ? <Spinner color="brand.500" /> : children}</Flex>;
}

/**
 * Contact cards table — handles loading / error / empty internally.
 * @param {Object} props - items, loading, error, onSelect
 * @returns {React.ReactElement}
 */
function ContactCardsTable({ items, loading, error, onSelect }) {
  const empty = !items || items.length === 0;
  const headColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  const errColor = useColorModeValue("red.500", "red.300");
  const emptyColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
  return (
    <Card px="0" py="0" overflow="hidden">
      {loading && empty ? <StateBox loading><span /></StateBox> : null}
      {!loading && error && empty ? (
        <StateBox><Text color={errColor} fontSize="sm">{typeof error === "string" ? error : "Erreur lors du chargement"}</Text></StateBox>
      ) : null}
      {!loading && !error && empty ? (
        <StateBox><Text color={emptyColor} fontSize="sm">Aucun contact à afficher</Text></StateBox>
      ) : null}
      {!empty ? (
        <TableContainer>
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>{COLUMNS.map((c) => <Th key={c} {...HEAD_CELL} color={headColor}>{c}</Th>)}</Tr>
            </Thead>
            <Tbody>
              {items.map((card) => <ContactRow key={card.id} card={card} onSelect={onSelect} />)}
            </Tbody>
          </Table>
        </TableContainer>
      ) : null}
    </Card>
  );
}

export default ContactCardsTable;
